import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { validateFileType } from './file-type-validation';
import { mapSecondsToTimeValue, mapTimeValueToSeconds } from './time-util';
import { validateFileDuration } from './file-duration-validation';

dayjs.extend(customParseFormat);

const messages = {
    WRONG_TIME: 'неверный формат длительности. Формат: ММ:СС. Пример: 00:49',
    WRONG_DATE: 'неверный формат даты. Формат: ДД.ММ.ГГГГ. Пример: 11.12.2000',
    WRONG_MAX_DATE: 'дата не может быть больше текущей',
    WRONG_MIN_DATE: 'нельзя быть на столько старым',
    WRONG_EMAIL: 'должно быть корректным емэйлом',
    WRONG_VKURL: 'неправильная ссылка на VK-профиль (пример: https://vk.com/test_profile)',
    WRONG_RUSTEXT: 'используйте только русские символы!',
    WRONG_PASS:
        'Пароль должен содержать только латиницу, цифры, спец символы и пробел, не менее 6 и не более 30 символов',
    TRANS_REQUIRED: 'Необходимо указать транскрипцию значения предыдущего поля',
    WRONG_TRANS: 'Транскрипция не должна содержать латинских букв',

    MAX_LEN_30: 'не должно превышать 30 символов',
    MUST_BE_SAME_PASS: 'Не совпадает с полем Пароль',
    MUST_BE_ACCEPT_RULES: 'Необходимо подтвердить согласие',
    MUST_BE_USER_ID: "Должно быть email'ом или числом",
    REQUIRED: 'Не может быть пустым',
};

const typeValidation = {
    time: (value) => {
        const match = value.match(/^(\d?\d):(\d\d?)$/);
        if (!match) return messages.WRONG_TIME;
        const minutes = +match[1];
        const seconds = +match[2];
        if (minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) return messages.WRONG_TIME;
        return null;
    },
    date: (value) => {
        const match = value.match(/^\d?\d\.\d\d?\.\d\d\d\d$/);
        if (!match) return messages.WRONG_DATE;
        const date = dayjs(value, 'DD.MM.YYYY');
        if (!date.isValid()) return messages.WRONG_DATE;
        if (date.isAfter(dayjs())) return messages.WRONG_MAX_DATE;
        if (date.isBefore(dayjs().subtract(100, 'year'))) return messages.WRONG_MIN_DATE;
        return null;
    },
    email: (value) => {
        const match = value.match(/^.+@.+\..+$/);
        if (!match) return messages.WRONG_EMAIL;
        return null;
    },
    vkUrl: (value) => {
        const match = value.match(/^(https?:\/\/)?(www\.)?vk\.com\/(id\d+|[0-9.A-Za-z_-]+)\/?$/);
        if (!match) return messages.WRONG_VKURL;
        return null;
    },
    rustext: (value) => {
        const match = value.match(/^[А-Яа-я -]+$/);
        if (!match) return messages.WRONG_RUSTEXT;
        return null;
    },
    password: (value) => {
        const match = value.match(/^[0-9A-Za-z_!@#$%^&*()-=+ ]{6,30}$/);
        if (!match) return messages.WRONG_PASS;
        return null;
    },
};

export class ValidationSchema {
    constructor(fields, params) {
        this.fields = {};
        (fields || []).forEach((field) => {
            const fieldName = field.code || field.id;
            this.fields[fieldName] = field;
        });

        this.validate = this.validate.bind(this);
        this.cast = this.cast.bind(this);
        this.onValidateFailed = null;
        this.params = params;
    }

    validate(values) {
        let failed = false;
        const res = Object.keys(this.fields).reduce((result, key) => {
            const errMess = validateField({
                field: this.fields[key],
                value: values && values[key],
                rootValue: values,
                params: this.params,
            });
            if (errMess) {
                failed = true;
                return {
                    ...result,
                    [key]: errMess,
                };
            }
            return result;
        }, {});
        if (failed && this.onValidateFailed) this.onValidateFailed();
        return res;
    }

    cast(values) {
        return Object.keys(this.fields).reduce((result, key) => {
            return {
                ...result,
                [key]: castField({ field: this.fields[key], value: values && values[key] }),
            };
        }, {});
    }
}

export function validateField({ field, value, rootValue, params }) {
    const fieldName = field.code || field.id;
    //console.log(fieldName, typeof fieldName);
    const checkedValue = castField({ field, value });

    if (fieldName === 'descTrans' && rootValue['role'] !== 'participant') return null;

    if (fieldName === 'acceptRules' && field.required && !checkedValue) return messages.MUST_BE_ACCEPT_RULES;

    if (fieldName === 'confirmPass' && checkedValue && (!rootValue || checkedValue !== rootValue.pass))
        return messages.MUST_BE_SAME_PASS;

    if (field.required && !checkedValue) return messages.REQUIRED;

    if (fieldName === 'userId' && checkedValue && !checkedValue.match(/(^.+@.+\..+$|^\d+$)/))
        return messages.MUST_BE_USER_ID;

    if (
        ['name', 'surname', 'firstname', 'patronimic', 'city', 'club'].includes(fieldName) &&
        checkedValue &&
        checkedValue.length > 30
    )
        return messages.MAX_LEN_30;

    if (/Trans$/.test(fieldName)) {
        //console.log('it`s trans', rootValue);
        const prevFieldId = fieldName.substring(0, fieldName.length - 5);
        if (field.required && !checkedValue && rootValue[prevFieldId] && /[A-Za-z]/.test(rootValue[prevFieldId])) {
            return messages.TRANS_REQUIRED;
        }
        if (checkedValue && /[A-Za-z]/.test(checkedValue)) {
            return messages.WRONG_TRANS;
        }
    }

    if (fieldName === 'oldPass') return null;

    const typeCheck = typeValidation[field.inputType] && typeValidation[field.inputType](checkedValue);
    if (typeCheck) return typeCheck;

    if (fieldName === 'time' && checkedValue) {
        const timeFieldValidateMessage = getTimeFieldValidateMessage(checkedValue, params);
        if (timeFieldValidateMessage) {
            return timeFieldValidateMessage;
        }
    }

    return null;
}

function getTimeFieldValidateMessage(value, nominationParams) {
    if (!nominationParams || !value) {
        return '';
    }
    const valueInSeconds = mapTimeValueToSeconds(value);
    if (
        (nominationParams.minDuration && valueInSeconds < nominationParams.minDuration) ||
        (nominationParams.maxDuration &&
            valueInSeconds > nominationParams.maxDuration + nominationParams.maxDurationDelta)
    ) {
        return getWrongTimeMessage(nominationParams);
    }
    return '';
}

function getWrongTimeMessage(params) {
    let message = 'Длительность ';
    if (params.minDuration) {
        message += `не должна быть меньше ${mapSecondsToTimeValue(params.minDuration)}`;
    }
    if (params.minDuration && params.maxDuration) {
        message += ` и `;
    }
    if (params.maxDuration) {
        message += `не должна превышать ${mapSecondsToTimeValue(params.maxDuration)}`;
    }
    return message;
}

export function castField({ field, value }) {
    if (typeof value === 'boolean') return value;
    if (value === undefined || value === null) return '';
    if (field.inputType === 'password') return value.toString();
    if (field.inputType === 'date') return value ? dayjs(value).format('DD.MM.YYYY') : '';
    if (['sex', 'gender', 'role'].includes(field.inputType) && !value) return '';
    return value.toString().trim();
}

export async function validateFile(file, request, fileType) {
    if (!file) {
        return 'Что-то не так';
    }
    console.log('validateFile', file, fileType, request);

    try {
        validateFileType(file, fileType, request);
    } catch (error) {
        return error.message;
    }

    if (['image/bmp', 'image/jpeg', 'image/png', 'image/tiff'].includes(file.type)) {
        if (file.size > 15 * 1024 * 1024) return 'Изображения должны быть не более 15 Мб';
        return null;
    }
    if (file.size > 800000000) {
        return 'Максимально допустимый размер файла 800000000 байт (чуть меньше 800 Мб)';
    }

    if (fileType.code === 'FONO') {
        try {
            await validateFileDuration(file, request);
        } catch (error) {
            return error.message;
        }
    }

    return null;
}

export function validateRequestUsers(request) {
    if (!request.users || request.users.length === 0)
        return 'В заявке не указано ни одного участника. Пожалуйста, исправьте это.';
    const usersCount = request.users.reduce((res, u) => (u.role === 'participant' ? res + 1 : res), 0);
    const minUsers = (request.nomination.params && request.nomination.params.minParticipants) || 0;
    const maxUsers = request.nomination.params && request.nomination.params.maxParticipants;
    if ((minUsers && usersCount < minUsers) || (maxUsers && usersCount > maxUsers)) {
        if (minUsers === maxUsers)
            return `В заявке должно быть участников: ${minUsers}. В заявке их: ${usersCount}. Пожалуйста, исправьте это.`;
        if (!maxUsers)
            return `В заявке должно быть участников не менее ${minUsers}. В заявке их: ${usersCount}. Пожалуйста, исправьте это.`;
        return `В заявке должно быть участников ${minUsers}-${maxUsers}. В заявке их: ${usersCount}. Пожалуйста, исправьте это.`;
    }
    if (usersCount === 0) {
        return `В заявке должен быть хотя бы один "участник". Пожалуйста, исправьте это.`;
    }
    return null;
}
